import { IPaginate, RootState, StoreState } from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { EventsManagementService } from '@/services/events-management.service';

export interface IManagedEvent {
  id: number;
  uuid: string;
  name: string;
  state: string;
  dispatchAttempts: number;
  senderAddress: string;
  executionTimestamp: string;
  executionTimeInMillis: number;
  useGlobal: boolean;
  createdAt: string;
  userId: number;
  userName: string;
  userEmail: string;
  recipientCounts: { [status: string]: number };
}

export interface IEventRecipient {
  id: number;
  phone: string;
  status: string;
  sequenceNo: string;
  attempts: number;
  lastError: string;
  updatedAt: string;
}

const state: StoreState<IManagedEvent> = {
  all: [],
  paginate: {
    total: 0,
    page: 1,
    pages: 1,
    itemsPerPage: 15,
  },
  recipients: [],
  recipientsPaginate: {
    total: 0,
    page: 1,
    pages: 1,
    itemsPerPage: 15,
  },
};

const mutations: MutationTree<StoreState<IManagedEvent>> = {
  UPDATE_LIST(state, payload: Array<IManagedEvent>) {
    state.all = payload;
  },
  UPDATE_PAGINATION(state, payload: IPaginate) {
    state.paginate = payload;
  },
  UPDATE_RECIPIENTS(state, payload: Array<IEventRecipient>) {
    state.recipients = payload;
  },
  UPDATE_RECIPIENTS_PAGINATION(state, payload: IPaginate) {
    state.recipientsPaginate = payload;
  },
};

const actions: ActionTree<StoreState<IManagedEvent>, RootState> = {
  async list({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await EventsManagementService.list(query);
      dispatch('isPageLoading', false, { root: true });
      const { docs, page, pages, total, limit: itemsPerPage } = response$.data.paginateObj;
      commit('UPDATE_LIST', docs);
      commit('UPDATE_PAGINATION', { page, pages, total, itemsPerPage });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async recipients({ commit, dispatch }, payload: { eventId: number; query: string }) {
    try {
      dispatch('isDialogLoading', true, { root: true });
      const response$ = await EventsManagementService.recipients(payload.eventId, payload.query);
      dispatch('isDialogLoading', false, { root: true });
      const { docs, page, pages, total, limit: itemsPerPage } = response$.data.paginateObj;
      commit('UPDATE_RECIPIENTS', docs);
      commit('UPDATE_RECIPIENTS_PAGINATION', { page, pages, total, itemsPerPage });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async requeue({ dispatch }, payload: { eventId: number; resetSentRecipients: boolean; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await EventsManagementService.requeue(payload.eventId, payload.resetSentRecipients);
      dispatch('isLoading', false, { root: true });
      dispatch(
        'snackBarMessage',
        `Event requeued for dispatch (${response$.data?.recipientsReset ?? 0} recipients reset)`,
        { root: true },
      );
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('list', payload.query);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
};

const getters: GetterTree<StoreState<IManagedEvent>, RootState> = {
  getAllEvents: state => state.all,
  getPagination: state => state.paginate,
  getRecipients: state => state.recipients,
  getRecipientsPagination: state => state.recipientsPaginate,
};

export const eventsManagement: Module<StoreState<IManagedEvent>, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
